import { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { QueryField, QueryRule } from '../../../../types/QueryBuilder';
import { useLanguage } from '../../../../context/LanguageContext';
import FieldRow from './SimpleQuery/FieldRow';
import SelectionSummary from './SimpleQuery/SelectionSummary';
import {
    COUNTER_FIELD_NAME,
    COUNTER_TYPE_FIELD_NAME,
    GDPR_CATEGORY_TITLE_KEY,
    MERGED_INTO_FIELD_NAMES,
    OPTIN_TO_STATUS_FIELD_NAME,
    groupFieldsBySubcategory,
    toHelpKey,
} from './SimpleQuery/fieldConfig';

interface SimpleQueryGroupProps {
    fields: QueryField[];
    rules: QueryRule[];
    onUpdateField: (fieldName: string, data: Partial<QueryRule>) => void;
}

function SimpleQueryGroup({ fields, rules, onUpdateField }: SimpleQueryGroupProps) {
    const { t } = useLanguage();
    // fields barely changes (it's the field catalog, not the user's picks) while rules changes on
    // every keystroke — memoizing avoids re-grouping the whole field list on each of those renders.
    const subcategoryGroups = useMemo(() => groupFieldsBySubcategory(fields), [fields]);

    return (
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#6c757d', fontWeight: 600, mb: 2 }}>
                {t('pages.clients.queryBuilder.simple.searchCriteria')}
            </Typography>

            {fields.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', color: '#6c757d', bgcolor: 'white', borderRadius: 1, border: '2px dashed #dee2e6' }}>
                    <Typography variant="body2">
                        {t('pages.clients.queryBuilder.simple.noFieldsAvailable')}
                    </Typography>
                </Box>
            ) : (
                subcategoryGroups.map(({ titleKey, fields: groupFields }, index) => (
                    <Box key={titleKey} sx={{ mb: index < subcategoryGroups.length - 1 ? 3 : 0 }}>
                        <Typography variant="body2" sx={{ color: '#556ee6', fontWeight: 700, mb: 0.5, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                            {t(titleKey)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: '#6c757d', mb: 1 }}>
                            {t(toHelpKey(titleKey))}
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: titleKey === GDPR_CATEGORY_TITLE_KEY
                                    ? { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }
                                    : { xs: '1fr', md: '1fr 1fr' },
                                alignItems: 'start',
                                gap: 1.5,
                            }}
                        >
                            {groupFields
                                .filter(field => !MERGED_INTO_FIELD_NAMES.has(field.name))
                                .map(field => {
                                    const rule = rules.find(r => r.field === field.name);
                                    const pairedFieldName = field.name === COUNTER_FIELD_NAME
                                        ? COUNTER_TYPE_FIELD_NAME
                                        : OPTIN_TO_STATUS_FIELD_NAME[field.name];
                                    const pairedField = pairedFieldName ? fields.find(f => f.name === pairedFieldName) : undefined;
                                    const pairedRule = pairedField ? rules.find(r => r.field === pairedField.name) : undefined;

                                    return (
                                        <FieldRow
                                            key={field.name}
                                            field={field}
                                            rule={rule}
                                            onUpdateField={onUpdateField}
                                            pairedField={pairedField}
                                            pairedRule={pairedRule}
                                            pairedIsOptinStatus={field.name in OPTIN_TO_STATUS_FIELD_NAME}
                                        />
                                    );
                                })}
                        </Box>
                    </Box>
                ))
            )}

            {fields.length > 0 && <SelectionSummary fields={fields} rules={rules} />}
        </Paper>
    );
}

export default SimpleQueryGroup;
